import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { _noop, _notNil } from '@/littledash';
import { useAddSamplePlanContext, usePlannedSamples } from '@/components/Samples/AddSamplePlanProvider';
import { SelectionValue } from '@/components/UI/FuzzySearchSelect/FuzzySearchSelect';
import {
  assignAnimalReducer,
  AssignAnimalState,
  assignAnimalStateInitializer,
} from '@/components/Samples/AssignAnimals/AssignAnimal.util';
import { isApiId } from '@/utils/ApiId.utils';
import {
  GroupTarget,
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionFormData,
  SampleDefinitionTargetFormData,
  TargetApiId,
} from '@/model/SamplePlan.model';

export type PlannedSampleOption = {
  api_id: SampleDefinitionFormData['api_id'];
  name: string;
  sample_name: string;
};

export type AssignAnimalsContext = AssignAnimalState & {
  plannedSampleOptions: Map<PlannedSampleOption['api_id'], PlannedSampleOption>;
  plannedAnimalsOptions: Map<PlannedAnimalTarget['api_id'], PlannedAnimalTarget>;
  registeredToPlannedMap: Map<RegisteredAnimalTarget['api_id'], PlannedAnimalTarget['api_id']>;
  updateActiveTargetType: (value: AssignAnimalsContext['activeTargetType']) => void;
  updateSelection: (
    action:
      | {
          type: 'target';
          targetType: AssignAnimalsContext['activeTargetType'];
          value: Map<TargetApiId, Exclude<SelectionValue, 'indeterminate'>>;
        }
      | {
          type: 'planned-sample';
          targetType: AssignAnimalsContext['activeTargetType'];
          value: Map<PlannedSampleOption['api_id'], Exclude<SelectionValue, 'indeterminate'>>;
        }
  ) => void;
};

const context = createContext<AssignAnimalsContext>({
  activeTargetType: 'groups',
  plannedSampleOptions: new Map(),
  plannedAnimalsOptions: new Map(),
  registeredToPlannedMap: new Map(),
  selectedGroupTargets: new Set(),
  selectedPlannedAnimalTargets: new Set(),
  selectedRegisteredAnimalTargets: new Set(),
  targetToPlannedSamples: new Map(),
  plannedSampleToTargets: new Map(),
  plannedSampleToTargetType: new Map(),
  updateActiveTargetType: _noop,
  updateSelection: _noop,
});

export const useAssignAnimalsContext = () => useContext(context);
const sampleOptionComparator = (
  left: [PlannedSampleOption['api_id'], PlannedSampleOption],
  right: [PlannedSampleOption['api_id'], PlannedSampleOption]
) => (left?.[1]?.name ?? '').localeCompare(right?.[1]?.name ?? '');

export const AssignAnimalsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { samplePlanState, sampleGlossary, planned_animals } = useAddSamplePlanContext();
  const plannedSamples = usePlannedSamples();

  const [state, dispatch] = useReducer(
    assignAnimalReducer,
    { assignAnimalFormData: samplePlanState.assignAnimalFormData },
    assignAnimalStateInitializer
  );

  const updateSelection = useCallback<AssignAnimalsContext['updateSelection']>(
    (action) => {
      switch (action.type) {
        case 'target': {
          switch (action.targetType) {
            case 'groups': {
              dispatch({
                type: 'update-group-target-selection',
                value: Array.from(action.value.entries()).reduce<Set<GroupTarget['api_id']>>(
                  (acc, [key, value]) => (isApiId('grp', key, true) && value === true ? acc.add(key) : acc),
                  new Set()
                ),
              });
              break;
            }
            case 'planned_animals': {
              dispatch({
                type: 'update-planned-animal-target-selection',
                value: Array.from(action.value.entries()).reduce<Set<PlannedAnimalTarget['api_id']>>(
                  (acc, [key, value]) => (isApiId('adf', key, true) && value === true ? acc.add(key) : acc),
                  new Set()
                ),
              });
              break;
            }
            case 'registered_animals': {
              dispatch({
                type: 'update-registered-animal-target-selection',
                value: Array.from(action.value.entries()).reduce<Set<RegisteredAnimalTarget['api_id']>>(
                  (acc, [key, value]) => (isApiId('aml', key, true) && value === true ? acc.add(key) : acc),
                  new Set()
                ),
              });
              break;
            }
          }
          break;
        }
        case 'planned-sample': {
          dispatch({ type: 'update-planned-sample-selection', value: action.value });
        }
      }
    },
    [dispatch]
  );
  const updateActiveTargetType = useCallback<AssignAnimalsContext['updateActiveTargetType']>(
    (value) => dispatch({ type: 'update-active-target-type', value }),
    []
  );
  const plannedSampleOptions = useMemo<AssignAnimalsContext['plannedSampleOptions']>(
    () =>
      new Map(
        Array.from(plannedSamples.values())
          .flatMap((sampleData) => {
            if (!isApiId('tgy', sampleData.type)) {
              return [];
            }
            const sample_name = sampleGlossary.get(sampleData.type)?.title;
            return _notNil(sample_name) && 'definitions' in sampleData
              ? sampleData.definitions.map<[PlannedSampleOption['api_id'], PlannedSampleOption]>((definition) => [
                  definition.api_id,
                  { api_id: definition.api_id, name: definition.name, sample_name },
                ])
              : [];
          })
          .sort(sampleOptionComparator)
      ),
    [plannedSamples, sampleGlossary]
  );
  const plannedAnimalsOptions = useMemo<AssignAnimalsContext['plannedAnimalsOptions']>(() => {
    const plannedAnimalOptions = new Map();
    for (const plannedAnimal of planned_animals.values()) {
      plannedAnimalOptions.set(plannedAnimal.api_id, plannedAnimal);
    }
    return plannedAnimalOptions;
  }, [planned_animals]);

  const registeredToPlannedMap = useMemo<AssignAnimalsContext['registeredToPlannedMap']>(() => {
    const map = new Map<RegisteredAnimalTarget['api_id'], PlannedAnimalTarget['api_id']>();
    for (const plannedAnimal of planned_animals.values()) {
      if (_notNil(plannedAnimal.animal_api_id)) {
        map.set(plannedAnimal.animal_api_id, plannedAnimal.api_id);
      }
    }
    return map;
  }, [planned_animals]);

  useEffect(() => {
    const targetAssignments = Array.from(plannedSampleOptions.keys()).reduce((acc, plannedSampleApiId) => {
      const type = state.plannedSampleToTargetType.get(plannedSampleApiId) ?? 'no_target';
      switch (type) {
        case 'groups': {
          return acc.set(plannedSampleApiId, {
            type,
            targets: [...(state.plannedSampleToTargets.get(plannedSampleApiId) ?? [])].filter((id) =>
              isApiId('grp', id, true)
            ),
          });
        }
        case 'planned_animals': {
          return acc.set(plannedSampleApiId, {
            type,
            targets: [...(state.plannedSampleToTargets.get(plannedSampleApiId) ?? [])].filter((id) =>
              isApiId('adf', id, true)
            ),
          });
        }
        case 'registered_animals': {
          return acc.set(plannedSampleApiId, {
            type,
            targets: [...(state.plannedSampleToTargets.get(plannedSampleApiId) ?? [])].filter((id) =>
              isApiId('aml', id, true)
            ),
          });
        }
        default:
          return acc.set(plannedSampleApiId, { type: 'no_target' });
      }
    }, new Map<SampleDefinitionFormData['api_id'], SampleDefinitionTargetFormData>());

    samplePlanState.setAnimalTargets(targetAssignments);
  }, [plannedSampleOptions, state.plannedSampleToTargets, state.plannedSampleToTargetType, samplePlanState]);

  const contextValue = useMemo<AssignAnimalsContext>(
    () => ({
      activeTargetType: state.activeTargetType,
      plannedSampleOptions,
      plannedAnimalsOptions,
      registeredToPlannedMap,
      selectedGroupTargets: state.selectedGroupTargets,
      selectedPlannedAnimalTargets: state.selectedPlannedAnimalTargets,
      selectedRegisteredAnimalTargets: state.selectedRegisteredAnimalTargets,
      targetToPlannedSamples: state.targetToPlannedSamples,
      plannedSampleToTargets: state.plannedSampleToTargets,
      plannedSampleToTargetType: state.plannedSampleToTargetType,
      updateSelection,
      updateActiveTargetType,
    }),
    [
      state.activeTargetType,
      plannedSampleOptions,
      plannedAnimalsOptions,
      registeredToPlannedMap,
      state.selectedGroupTargets,
      state.selectedPlannedAnimalTargets,
      state.selectedRegisteredAnimalTargets,
      state.targetToPlannedSamples,
      state.plannedSampleToTargets,
      state.plannedSampleToTargetType,
      updateSelection,
      updateActiveTargetType,
    ]
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
